/**
 * InstrumentParameters Component
 * Provides dynamic parameter controls for instrument sound customization
 */

import { memo, useMemo, useCallback } from 'react';
import type { InstrumentType } from '../../types/composition';
import type {
  InstrumentParameters as IParams,
  InstrumentMetadata,
  ADSRSettings,
  OscillatorSettings,
  FilterSettings,
  EffectsSettings,
} from '../../types/instruments';
import { instrumentDatabase } from '../../services/instruments/InstrumentDatabase';
import { InstrumentErrorBoundary } from '../ErrorBoundary';
import { validateInstrumentParameters } from '../../utils/parameterValidation';
import './InstrumentParameters.css';

/**
 * Props for InstrumentParameters component
 */
interface InstrumentParametersProps {
  /** Current instrument type */
  instrument: InstrumentType;
  /** Current parameter values */
  parameters: IParams;
  /** Callback when parameters change */
  onChange: (parameters: IParams) => void;
  /** Whether parameters are enabled */
  parametersEnabled: boolean;
  /** Callback when parameters enabled state changes */
  onParametersEnabledChange: (enabled: boolean) => void;
  /** Row index for accessibility */
  row: number;
  /** Whether the panel is expanded (controlled from parent) */
  isExpanded?: boolean;
}

/**
 * Props for parameter slider control
 */
interface SliderControlProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  onChange: (value: number) => void;
  description?: string;
}

/**
 * Slider control component
 * Memoized to prevent unnecessary re-renders
 */
const SliderControl = memo(function SliderControl({
  label,
  value,
  min,
  max,
  step,
  unit = '',
  onChange,
  description,
}: SliderControlProps) {
  return (
    <div className="param-control">
      <label className="param-label" title={description}>
        <span className="param-name">{label}</span>
        <span className="param-value">
          {value.toFixed(step < 0.01 ? 3 : step < 0.1 ? 2 : step < 1 ? 1 : 0)}
          {unit}
        </span>
      </label>
      <input
        type="range"
        className="param-slider"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        aria-label={`${label} ${value}${unit}`}
      />
    </div>
  );
});

/**
 * ADSR Controls sub-component
 * Memoized to prevent unnecessary re-renders
 */
const ADSRControls = memo(function ADSRControls({
  adsr,
  onChange,
}: {
  adsr: ADSRSettings;
  onChange: (adsr: ADSRSettings) => void;
}) {
  return (
    <div className="param-section">
      <h4 className="param-section-title">ADSR Envelope</h4>
      <div className="param-grid">
        <SliderControl
          label="Attack"
          value={adsr.attack}
          min={0.001}
          max={2.0}
          step={0.001}
          unit="s"
          onChange={(attack) => onChange({ ...adsr, attack })}
          description="Attack time - how quickly the sound reaches full volume"
        />
        <SliderControl
          label="Decay"
          value={adsr.decay}
          min={0.001}
          max={2.0}
          step={0.001}
          unit="s"
          onChange={(decay) => onChange({ ...adsr, decay })}
          description="Decay time - how quickly the sound drops to sustain level"
        />
        <SliderControl
          label="Sustain"
          value={adsr.sustain}
          min={0.0}
          max={1.0}
          step={0.01}
          onChange={(sustain) => onChange({ ...adsr, sustain })}
          description="Sustain level - the volume level while note is held"
        />
        <SliderControl
          label="Release"
          value={adsr.release}
          min={0.001}
          max={2.0}
          step={0.001}
          unit="s"
          onChange={(release) => onChange({ ...adsr, release })}
          description="Release time - how quickly the sound fades after note ends"
        />
      </div>
    </div>
  );
});

/**
 * Oscillator Controls sub-component
 * Memoized to prevent unnecessary re-renders
 */
const OscillatorControls = memo(function OscillatorControls({
  oscillator,
  onChange,
  instrumentType,
}: {
  oscillator: OscillatorSettings;
  onChange: (osc: OscillatorSettings) => void;
  instrumentType: InstrumentType;
}) {
  // Check if this is an FM synth
  const isFM = instrumentType === 'fmsynth' || instrumentType === 'metal' || instrumentType === 'sfx-powerup';

  if (isFM) {
    return (
      <div className="param-section">
        <h4 className="param-section-title">FM Synthesis</h4>
        <div className="param-grid">
          {oscillator.harmonicity !== undefined && (
            <SliderControl
              label="Harmonicity"
              value={oscillator.harmonicity}
              min={0.5}
              max={8}
              step={0.1}
              onChange={(harmonicity) => onChange({ ...oscillator, harmonicity })}
              description="Frequency ratio between carrier and modulator"
            />
          )}
          {oscillator.modulationIndex !== undefined && (
            <SliderControl
              label="Mod Index"
              value={oscillator.modulationIndex}
              min={0}
              max={20}
              step={0.5}
              onChange={(modulationIndex) => onChange({ ...oscillator, modulationIndex })}
              description="Amount of frequency modulation"
            />
          )}
        </div>
      </div>
    );
  }

  // Standard oscillator controls
  if (oscillator.pulseWidth !== undefined) {
    return (
      <div className="param-section">
        <h4 className="param-section-title">Oscillator</h4>
        <div className="param-grid">
          <SliderControl
            label="Pulse Width"
            value={oscillator.pulseWidth}
            min={0.0}
            max={1.0}
            step={0.01}
            onChange={(pulseWidth) => onChange({ ...oscillator, pulseWidth })}
            description="Pulse wave duty cycle"
          />
        </div>
      </div>
    );
  }

  return null;
});

/**
 * Filter Controls sub-component
 * Memoized to prevent unnecessary re-renders
 */
const FilterControls = memo(function FilterControls({
  filter,
  onChange,
}: {
  filter: FilterSettings;
  onChange: (filter: FilterSettings) => void;
}) {
  if (!filter.frequency) return null;

  return (
    <div className="param-section">
      <h4 className="param-section-title">Filter</h4>
      <div className="param-grid">
        <SliderControl
          label="Cutoff"
          value={filter.frequency}
          min={50}
          max={10000}
          step={10}
          unit="Hz"
          onChange={(frequency) => onChange({ ...filter, frequency })}
          description="Filter cutoff frequency"
        />
        {filter.resonance !== undefined && (
          <SliderControl
            label="Resonance"
            value={filter.resonance}
            min={0.1}
            max={10}
            step={0.1}
            onChange={(resonance) => onChange({ ...filter, resonance })}
            description="Filter resonance/Q factor"
          />
        )}
        {filter.envelopeAmount !== undefined && (
          <SliderControl
            label="Env Amount"
            value={filter.envelopeAmount}
            min={0}
            max={1}
            step={0.01}
            onChange={(envelopeAmount) => onChange({ ...filter, envelopeAmount })}
            description="Amount of envelope modulation on filter"
          />
        )}
      </div>
    </div>
  );
});

/**
 * Effects Controls sub-component
 * Memoized to prevent unnecessary re-renders
 */
const EffectsControls = memo(function EffectsControls({
  effects,
  onChange,
}: {
  effects: EffectsSettings;
  onChange: (effects: EffectsSettings) => void;
}) {
  const hasEffects =
    effects.bitCrush !== undefined ||
    effects.distortion !== undefined ||
    effects.chorus !== undefined;

  if (!hasEffects) return null;

  return (
    <div className="param-section">
      <h4 className="param-section-title">Effects</h4>
      <div className="param-grid">
        {effects.bitCrush !== undefined && (
          <SliderControl
            label="Bit Crush"
            value={effects.bitCrush}
            min={1}
            max={16}
            step={1}
            unit=" bits"
            onChange={(bitCrush) => onChange({ ...effects, bitCrush })}
            description="Bit depth reduction for lo-fi effect"
          />
        )}
        {effects.distortion !== undefined && (
          <SliderControl
            label="Distortion"
            value={effects.distortion}
            min={0}
            max={1}
            step={0.01}
            onChange={(distortion) => onChange({ ...effects, distortion })}
            description="Distortion amount"
          />
        )}
        {effects.chorus !== undefined && (
          <div className="param-control">
            <label className="param-label param-checkbox-label">
              <input
                type="checkbox"
                checked={effects.chorus}
                onChange={(e) => onChange({ ...effects, chorus: e.target.checked })}
                aria-label="Chorus effect"
              />
              <span className="param-name">Chorus</span>
            </label>
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * Main InstrumentParameters component
 * Optimized with React.memo and useMemo for performance
 */
function InstrumentParametersComponent({
  instrument,
  parameters,
  onChange,
  parametersEnabled,
  onParametersEnabledChange,
  isExpanded = true,
}: InstrumentParametersProps) {
  // Memoize instrument metadata lookup
  const metadata: InstrumentMetadata | undefined = useMemo(
    () => instrumentDatabase.getInstrument(instrument),
    [instrument]
  );

  // Memoize current parameters with defaults
  const currentParams: IParams = useMemo(
    () => ({
      adsr: parameters.adsr || { attack: 0.01, decay: 0.1, sustain: 0.7, release: 0.1 },
      oscillator: parameters.oscillator || {},
      filter: parameters.filter || {},
      effects: parameters.effects || {},
    }),
    [parameters]
  );

  /**
   * Reset to default parameters
   */
  const handleReset = useCallback(() => {
    if (metadata) {
      onChange(metadata.defaultParameters);
    }
  }, [metadata, onChange]);

  /**
   * Handle ADSR changes with validation
   */
  const handleADSRChange = useCallback(
    (adsr: ADSRSettings) => {
      try {
        const validated = validateInstrumentParameters({ ...currentParams, adsr });
        onChange(validated);
      } catch (error) {
        console.error('Error updating ADSR:', error);
      }
    },
    [currentParams, onChange]
  );

  /**
   * Handle oscillator changes with validation
   */
  const handleOscillatorChange = useCallback(
    (oscillator: OscillatorSettings) => {
      try {
        const validated = validateInstrumentParameters({ ...currentParams, oscillator });
        onChange(validated);
      } catch (error) {
        console.error('Error updating oscillator:', error);
      }
    },
    [currentParams, onChange]
  );

  /**
   * Handle filter changes with validation
   */
  const handleFilterChange = useCallback(
    (filter: FilterSettings) => {
      try {
        const validated = validateInstrumentParameters({ ...currentParams, filter });
        onChange(validated);
      } catch (error) {
        console.error('Error updating filter:', error);
      }
    },
    [currentParams, onChange]
  );

  /**
   * Handle effects changes with validation
   */
  const handleEffectsChange = useCallback(
    (effects: EffectsSettings) => {
      try {
        const validated = validateInstrumentParameters({ ...currentParams, effects });
        onChange(validated);
      } catch (error) {
        console.error('Error updating effects:', error);
      }
    },
    [currentParams, onChange]
  );

  if (!metadata) return null;

  return (
    <div className="instrument-parameters">
      {isExpanded && (
        <div className="param-panel">
          <div className="param-header">
            <div className="param-enable-row">
              <label className="param-enable-label">
                <input
                  type="checkbox"
                  className="param-enable-checkbox"
                  checked={parametersEnabled}
                  onChange={(e) => onParametersEnabledChange(e.target.checked)}
                  aria-label="Enable custom parameters"
                  title="Enable to customize sound parameters, disable to use default instrument sound"
                />
                <span className="param-enable-text">
                  Enable Custom Parameters
                </span>
              </label>
              <button
                className="param-reset-btn"
                onClick={handleReset}
                aria-label="Reset to default parameters"
                title="Reset all parameters to instrument defaults"
                disabled={!parametersEnabled}
              >
                Reset
              </button>
            </div>
          </div>

          <div className={`param-content ${!parametersEnabled ? 'param-content-disabled' : ''}`}>
            {!parametersEnabled && (
              <div className="param-disabled-message">
                <p>Enable custom parameters above to adjust sound settings.</p>
                <p className="param-disabled-hint">
                  The instrument will use its default sound when parameters are disabled.
                </p>
              </div>
            )}
            
            {parametersEnabled && (
              <>
                {/* ADSR Controls - Always shown for melodic instruments */}
                {currentParams.adsr && (
                  <ADSRControls adsr={currentParams.adsr} onChange={handleADSRChange} />
                )}

                {/* Oscillator Controls - Shown for instruments with oscillator settings */}
                {currentParams.oscillator && (
                  <OscillatorControls
                    oscillator={currentParams.oscillator}
                    onChange={handleOscillatorChange}
                    instrumentType={instrument}
                  />
                )}

                {/* Filter Controls - Shown for instruments with filter settings */}
                {currentParams.filter && (
                  <FilterControls filter={currentParams.filter} onChange={handleFilterChange} />
                )}

                {/* Effects Controls - Shown for instruments with effects */}
                {currentParams.effects && (
                  <EffectsControls effects={currentParams.effects} onChange={handleEffectsChange} />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Export memoized version wrapped in error boundary
export const InstrumentParameters = memo(function InstrumentParametersWithBoundary(props: InstrumentParametersProps) {
  return (
    <InstrumentErrorBoundary>
      <InstrumentParametersComponent {...props} />
    </InstrumentErrorBoundary>
  );
});
